import React, {useEffect, useState} from 'react';

import uniqBy from 'lodash/uniqBy';
import styled from 'styled-components';

import {InfiniteListRTK} from 'app/general/datafetchers/InfiniteListRTK';
import {PopoverForm} from 'app/general/overlays';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import StateButton from 'app/general/widgets/StateButton';
import {gt} from 'app/utils/i18n';

import {SimpleItemRenderItems} from './SimpleItemRenderItems';

import type {OverlayProps} from 'app/general/overlays';
import type {UseListQueryType} from 'app/store/sweet-api/api-query';

export const StyledBulkActionDiv = styled.div`
  letter-spacing: -0.25pt;
  font-size: 80%;
  text-transform: uppercase;
  font-weight: bold;

  label {
    padding: 0 5px;
  }
`;

type CommonProps<
  Item extends BaseItemPickerObject,
  FetchParameters extends {page: string}
> = {
  searchPlaceholder: string;
  useDataHook: UseListQueryType<FetchParameters, Item>;
  paramsConverter: (query: Record<string, any>) => FetchParameters;
  pageParam?: string;
  additionalParams?: Record<string, string | number>;
  addItemProps: (item: Item) => {
    disabledText?: string;
  };
  showDescription: boolean;
  isPopover?: boolean;
  isSmall?: boolean;
  children?: (
    selected: Array<Item>,
    refetch: () => void,
    onChange: (
      selected: Array<Item>
    ) => unknown | ((selected: Array<Item>) => Array<Item>)
  ) => React.ReactNode;
};

type WithStateProps<
  Item extends BaseItemPickerObject,
  FetchParameters extends {page: string}
> = CommonProps<Item, FetchParameters> & {
  overlayProps: OverlayProps;
  onConfirm: (selected: Array<Item>) => unknown;
};

export type BaseItemPickerObject =
  | {
      name: string;
      content_type: 'team';
      url: string;
      description?: string;
      id: number;
    }
  | {
      name: string;
      content_type:
        | 'document'
        | 'folder'
        | 'policy'
        | 'quiz'
        | 'user'
        | 'procedure'
        | 'proceduregroup';
      url: string;
      id: number | string;
    };

export function SimpleItemPickerListPopover<
  Item extends BaseItemPickerObject,
  FetchParameters extends {page: string}
>({
  overlayProps,
  onConfirm,
  children,
  ...other
}: WithStateProps<Item, FetchParameters>) {
  const [selected, setSelected] = useState<Array<Item>>([]);

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({closeOnSuccess, close, refreshPosition}) => (
        <StatelessSimpleItemPickerList
          selected={selected}
          setSelected={setSelected}
          {...other}
        >
          {(selected, refetchData, setSelected) => {
            refreshPosition();
            return (
              <>
                {children != null
                  ? children(selected, refetchData, setSelected)
                  : null}
                <div className="actions-block d-flex justify-content-between">
                  <button
                    className="btn btn-link text-secondary"
                    onClick={close}
                  >
                    <i className="fa fa-times mr-1" />
                    {gt('Cancel')}
                  </button>
                  <StateButton
                    disabled={selected.length === 0}
                    busyText={gt('Confirm')}
                    className="btn btn-sm btn-primary"
                    onClick={() => closeOnSuccess(onConfirm(selected))}
                  >
                    {gt('Confirm')}
                  </StateButton>
                </div>
              </>
            );
          }}
        </StatelessSimpleItemPickerList>
      )}
    </PopoverForm>
  );
}

type WithoutStateProps<
  IT extends BaseItemPickerObject,
  FP extends {page: string}
> = CommonProps<IT, FP> & {
  selected: Array<IT>;
  setSelected: (
    selected: Array<IT> | ((selected: Array<IT>) => Array<IT>)
  ) => unknown;
};

export function StatelessSimpleItemPickerList<
  IT extends BaseItemPickerObject,
  FP extends {page: string}
>({
  searchPlaceholder,
  children,
  selected,
  setSelected,
  showDescription,
  additionalParams = {},
  addItemProps = () => Object.freeze({}),
  isPopover = true,
  isSmall = false,
  useDataHook,
  paramsConverter,
  pageParam,
}: WithoutStateProps<IT, FP>) {
  const [search, setSearch] = useState<string>('');

  return (
    <InfiniteListRTK
      fetchParams={{...additionalParams, search}} // It's OK to pass a new object here, because
      // we are relying on ``fetchParams`` stringification in useInfiniteListFetcherRTK
      useDataHook={useDataHook}
      paramsConverter={paramsConverter}
      pageParam={pageParam}
    >
      {({
        hasNext,
        isLoading,
        loadNext,
        totalCount,
        results,
        isFetching,
        refetch,
      }) => (
        <StyledConstrainedHeightPopoverDiv
          className={isPopover == true ? 'popover-container' : ''}
        >
          <div className="content">
            {search !== '' || (!isLoading && results.length > 9) ? (
              <div className="border-bottom">
                <SearchInputField
                  autoFocus={true}
                  placeholder={searchPlaceholder}
                  onChange={setSearch}
                />
              </div>
            ) : null}
            {isLoading ? (
              <div className="border-bottom font-italic text-muted p-3 text-center">
                <i className="fa fa-circle-notch fa-spin" /> {gt('Loading')}
              </div>
            ) : (
              <>
                <BulkAction
                  addItemProps={addItemProps}
                  results={results}
                  selected={selected}
                  setSelected={setSelected}
                />
                <div className="scrollable border-bottom flex-grow-1">
                  <StyledPickerUL isSmall={isSmall}>
                    <SimpleItemRenderItems
                      showDescription={showDescription}
                      allResults={results}
                      selected={selected}
                      setSelected={setSelected}
                      addItemProps={addItemProps}
                    />
                    {totalCount === 0 ? (
                      <li>
                        <div className="font-italic text-muted p-3 text-center">
                          {gt('No results found')}
                        </div>
                      </li>
                    ) : isFetching ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm btn-block"
                      >
                        <i className="fa fa-circle-notch fa-spin" />{' '}
                        {gt('Loading...')}
                      </button>
                    ) : hasNext ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm btn-block"
                        onClick={loadNext}
                      >
                        {gt('Load more')}
                      </button>
                    ) : null}
                  </StyledPickerUL>
                </div>
              </>
            )}
            {children != null ? children(selected, refetch, setSelected) : null}
          </div>
        </StyledConstrainedHeightPopoverDiv>
      )}
    </InfiniteListRTK>
  );
}

type BulkActionProps<T> = {
  results: Array<T>;
  selected: Array<T>;
  setSelected: (items: Array<T> | ((items: Array<T>) => Array<T>)) => void;
  addItemProps: (item: T) => {
    disabledText?: string;
  };
};

function BulkAction<T extends BaseItemPickerObject>({
  addItemProps,
  results,
  selected,
  setSelected,
}: BulkActionProps<T>) {
  if (results.length < 2) {
    return null;
  }

  const selectedLen = selected.length;

  if (results.length === 0 && selectedLen === 0) {
    return null;
  }

  const selectedURLs = selected.map((item) => item.url);
  // filter out items which appears in more than one page
  // i.e. data was changed while paginating
  const availableItems = uniqBy(
    results.filter(
      (item) =>
        addItemProps(item).disabledText == null &&
        selectedURLs.indexOf(item.url) === -1
    ),
    'id'
  );
  // ref required for setting `indeterminate` on input.
  const bulkCheckbox = React.createRef<HTMLInputElement>();

  const action =
    availableItems.length === 0
      ? {
          checked: true,
          text: gt('Unselect all'),
          handler: () => setSelected([]),
        }
      : {
          checked: false,
          text: gt('Select all'),
          handler: () =>
            setSelected((selected) => [...selected, ...availableItems]),
        };

  useEffect(() => {
    if (bulkCheckbox.current != null) {
      bulkCheckbox.current.indeterminate = !action.checked && selectedLen > 0;
    }
  }, [action.checked, selectedLen]);

  return (
    <div className="actions-block border-bottom">
      <StyledBulkActionDiv className="d-flex justify-content-between">
        <label className="checkbox mb-0">
          <input
            className="mr-2"
            type="checkbox"
            ref={bulkCheckbox}
            checked={action.checked}
            onChange={action.handler}
          />
          {action.text}
        </label>
        <div className="text-muted">{selectedLen}</div>
      </StyledBulkActionDiv>
    </div>
  );
}

const StyledConstrainedHeightPopoverDiv = styled.div`
  > .content {
    > * {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
  }

  &.popover-container {
    overflow: hidden; // ie asked for it

    > .content {
      width: 250px;
      max-height: calc(99vh - 138px);

      // hack to make flex work on IE11
      @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
        height: calc(95vh - 20px);
      }

      display: flex;
      flex-direction: column;

      .scrollable {
        overflow: auto;
        -webkit-overflow-scrolling: touch;
      }

      > * {
        padding-left: 0.5rem;
        padding-right: 0.5rem;
      }

      .actions-block {
        border-bottom: 1px solid #dee2e6;
        text-align: right;

        > button {
          font-size: 0.8rem;
        }
      }
    }
  }
`;

type StyledPickerProps = {
  isSmall?: boolean;
};

export const StyledPickerUL = styled.ul<StyledPickerProps>`
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    border-bottom: 1px solid #eee;
    transition: background-color 0.5s;
    line-height: 2rem;
    ${({isSmall}) => (isSmall ? 'font-size: .875rem;' : '')}

    label {
      display: flex;
      margin: 0;
      font-weight: normal;
      padding: 0 5px;

      .description {
        margin: -0.25rem 0 0.25rem 0;
        line-height: 1.5;
      }
    }

    &.selected {
      background-color: #00a7c0 !important;
      color: #fff;
    }

    &:hover {
      background-color: #e9f7f9;
    }

    &.empty {
      color: #999;
      font-style: italic;
      border-bottom: none;
      text-align: center;
    }
  }
`;
